import { useState } from "react";
import { Share2, Copy, Check, Twitter, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function SharePage() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const handle = (user?.email || "friend").split("@")[0];
  const link = `${window.location.origin}/?ref=${encodeURIComponent(handle)}`;
  const text = `Join me on Blessed — kindness on the map. Bless a neighbor near you 💜`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Could not copy.");
    }
  };

  const options = [
    { label: "Twitter / X", icon: Twitter, color: "#1D9BF0", url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}` },
    { label: "WhatsApp", icon: MessageCircle, color: "#25D366", url: `https://wa.me/?text=${encodeURIComponent(text + " " + link)}` },
    { label: "iMessage / SMS", icon: MessageCircle, color: "#34C759", url: `sms:?&body=${encodeURIComponent(text + " " + link)}` },
    { label: "Email", icon: Mail, color: "#EA4335", url: `mailto:?subject=${encodeURIComponent("Join me on Blessed")}&body=${encodeURIComponent(text + "\n\n" + link)}` },
  ];

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Blessed", text, url: link });
      } catch (e) { /* cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6 pb-28">
      <div className="flex items-center gap-2 mb-1">
        <Share2 className="text-brand-primary" size={24} strokeWidth={2.6} />
        <h1 className="text-2xl font-black">Share Blessed</h1>
      </div>
      <p className="text-sm text-ink-tertiary font-semibold mb-6">Invite friends and grow the kindness in your neighborhood.</p>

      <div className="rounded-lg p-6 mb-6 text-white text-center" style={{ background: "linear-gradient(135deg,#1E40AF,#5B21B6)" }}>
        <div className="text-sm font-bold text-white/80">Your referral link</div>
        <div className="mt-2 font-black break-all text-lg">{link}</div>
        <Button data-testid="copy-link-button" onClick={copy} className="mt-4 h-11 rounded-pill font-extrabold bg-white text-brand-primary hover:bg-white/90">
          {copied ? <><Check size={18} className="mr-1" /> Copied</> : <><Copy size={18} className="mr-1" /> Copy link</>}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {options.map((o) => (
          <a
            key={o.label}
            data-testid={`share-${o.label.split(" ")[0].toLowerCase()}`}
            href={o.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-surface-2 border border-border rounded-md p-4 hover:bg-surface-3 transition-colors"
          >
            <div className="w-10 h-10 rounded-pill grid place-items-center text-white" style={{ background: o.color }}>
              <o.icon size={20} />
            </div>
            <span className="font-extrabold text-sm">{o.label}</span>
          </a>
        ))}
      </div>

      <Button data-testid="native-share-button" onClick={nativeShare} className="w-full h-12 rounded-pill font-extrabold bg-brand-primary text-white">
        <Share2 size={18} className="mr-1" /> Share…
      </Button>
    </div>
  );
}
